import React, { Fragment, useEffect, useState, useCallback } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Divider, Typography } from "@mui/material";
import theme from "../../theme";
import { getUpcomgAlert } from "../../api/adminAlertApi";
import { setLoading } from "../../features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { useAlertSocket } from "./useAlertSocket"; // Adjust the import path as needed

const DcpInsight = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);
    const { user } = useSelector((state) => state.auth); // Assuming user data is in auth state
    const [upcomingAlerts, setAlerts] = useState([]);

    const getStudentAlert = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getUpcomgAlert();
            setAlerts(response.data);
            dispatch(setLoading({ loading: false }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message ||
                err.response?.data?.message ||
                err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    }

    // Function to get styles based on priority
    const getPriorityStyles = (priority) => {
        switch (priority?.toUpperCase()) {
            case 'HIGH':
                return {
                    background: '#FFF7ED',
                    border: '1px solid #FFF7ED',
                    textColor: '#9F2D00',
                };
            case 'MEDIUM':
                return {
                    background: theme.palette.cardbackground.secondary,
                    border: `1px solid ${theme.palette.cardbackground.main}`,
                    textColor: theme.palette.text.dark,
                };
            case 'LOW':
                return {
                    background: theme.palette.cardbackground.primary,
                    border: `1px solid ${theme.palette.cardbackground.light}`,
                    textColor: theme.palette.cardbackground.middle,
                };
            default:
                return {
                    background: '#F5F5F5',
                    border: '1px solid #E0E0E0',
                    textColor: '#616161',
                };
        }
    }

    // Socket event handlers
    const handleNewAlert = useCallback((newAlert) => {
        setAlerts(prevAlerts => [newAlert, ...prevAlerts]);
    }, []);

    const handleUpdateAlert = useCallback((updatedAlert) => {
        setAlerts(prevAlerts =>
            prevAlerts.map(alert =>
                alert.alert_id === updatedAlert.alert_id ? updatedAlert : alert
            )
        );
    }, []);

    const handleDeleteAlert = useCallback((deletedAlert) => {
        setAlerts(prevAlerts =>
            prevAlerts.filter(alert => alert.alert_id !== deletedAlert.alert_id)
        );
    }, []);

    // Initialize socket connection
    useAlertSocket(user?.student_id, {
        onNew: handleNewAlert,
        onUpdate: handleUpdateAlert,
        onDelete: handleDeleteAlert
    });

    useEffect(() => {
        getStudentAlert();
    }, []);

    // Optional: Sort alerts by priority (HIGH first, then MEDIUM, then LOW)
    const sortedAlerts = [...upcomingAlerts].sort((a, b) => {
        const priorityOrder = { 'HIGH': 1, 'MEDIUM': 2, 'LOW': 3 };
        return (priorityOrder[a.priority?.toUpperCase()] || 4) -
            (priorityOrder[b.priority?.toUpperCase()] || 4);
    });

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonCard
                borderRadius={2}
                headerContent={
                    <Fragment>
                        <Box sx={{ padding: '17.5px 13px' }}>
                            <Typography fontWeight={600}>DCP Alerts</Typography>
                            <Typography fontSize={14} color={theme.palette.background.dark}>Important notifications to help you stay on track.</Typography>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    upcomingAlerts.length > 0 ?
                        (<Box
                            sx={{
                                height: '22vh',
                                overflow: 'auto',
                                '&::-webkit-scrollbar': {
                                    width: '6px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: '#f1f1f1',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: '#888',
                                    borderRadius: '3px',
                                },
                            }}
                        >
                            {sortedAlerts.map((alert, index) => {
                                const styles = getPriorityStyles(alert.priority);
                                return (
                                    <CommonCard
                                        key={index}
                                        background={styles.background}
                                        border={styles.border}
                                        borderRadius={1}
                                        paddingBottom="0px"
                                        padding="10px 10px"
                                        marginTop="10px"
                                        content={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography
                                                    color={styles.textColor}
                                                    sx={{
                                                        flex: 1,
                                                    }}
                                                >
                                                    {alert.title}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                )
                            })}
                        </Box>) : (
                            <Box
                                display="flex"
                                justifyContent="center"
                                p={2}
                            >
                                <Typography color={theme.palette.background.dark}>No Data Found</Typography>
                            </Box>
                        )
                }
                paddingBottom="0px"
                padding="13px"
                marginTop="15px"
            />
        </Fragment>
    )
}

export default DcpInsight;